import { KeyIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardBody, CardHeader, Input, Spinner } from '@nextui-org/react';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { PasswordInput } from '@portal/components/common/PasswordInput.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { tsrestClient } from '@portal/provider/tsrest';
import { useEncryptionStatus } from '@portal/utils/useEncryptionStatus';

const generateKeySchema = z
  .object({
    masterPassword: z.string().min(8, 'Master password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine(data => data.masterPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type GenerateKeyForm = z.infer<typeof generateKeySchema>;

/**
 * Key Generation Button Component
 *
 * Simple button that can trigger modal opening
 */
export const KeyGenerationButton = ({ onOpen }: { onOpen: () => void }) => {
  return (
    <Button color='primary' variant='shadow' size='lg' onPress={onOpen} className='w-full max-w-md'>
      <KeyIcon className='h-5 w-5 mr-2' />
      Generate Encryption Key
    </Button>
  );
};

/**
 * Key Generation Modal Component
 *
 * Always mounted modal for key generation
 */
export const KeyGenerationModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  const { forceRefetch } = useEncryptionStatus();

  const generateKeyMutation = tsrestClient.encryption.generateKey.useMutation({
    onSuccess: async data => {
      setRecoveryCode(data.body.recoveryCode);
      toast('Encryption key generated successfully!', { type: 'success' });

      // Refetch status immediately to update the system state
      await forceRefetch();

      // Set a temporary flag to prevent AuthGuard from redirecting during setup
      localStorage.setItem('encryption_setup_in_progress', 'true');
    },
    onError: error => {
      toast(
        <>
          <TypographyUI className='font-bold'>Failed to generate encryption key</TypographyUI>
          <TypographyUI className='text-ts'>error: {(error.body as any).message}</TypographyUI>
        </>,
        { type: 'error' }
      );
    },
  });

  const form = useForm<GenerateKeyForm>({
    resolver: zodResolver(generateKeySchema),
    mode: 'all',
    defaultValues: {
      masterPassword: '',
      confirmPassword: '',
    },
  });

  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
  } = form;

  const onSubmit = async (data: GenerateKeyForm) => {
    setIsGenerating(true);
    try {
      await generateKeyMutation.mutateAsync({
        body: {
          masterPassword: data.masterPassword,
          confirmPassword: data.confirmPassword,
        },
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setRecoveryCode(null);
    setHasConfirmed(false);
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    setHasConfirmed(true);
    // Remove the temporary flag so AuthGuard can redirect normally
    localStorage.removeItem('encryption_setup_in_progress');
    // Refetch status again to ensure we have the latest state
    await forceRefetch();
    handleClose();
  };

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={() => onClose()}
      title={recoveryCode ? 'Key Generated Successfully' : 'Generate Encryption Key'}
      subtitle={
        recoveryCode
          ? 'Your encryption key has been generated. Please save the recovery code.'
          : 'Create a new encryption key to secure your system data.'
      }
      icon={<KeyIcon className='h-5 w-5' />}
      size='2xl'
      footer={
        recoveryCode ? (
          <div className='flex gap-2'>
            <Button color='primary' onPress={handleConfirm} isLoading={hasConfirmed}>
              {hasConfirmed ? 'Proceeding...' : 'I Have Stored the Recovery Code'}
            </Button>
          </div>
        ) : (
          <>
            <Button color='danger' variant='light' onPress={handleClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              isLoading={isGenerating}
              isDisabled={!isValid || !isDirty}
              onClick={handleSubmit(onSubmit)}
            >
              Generate Key
            </Button>
          </>
        )
      }
    >
      {recoveryCode ? (
        <div className='space-y-3'>
          <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
            <TypographyUI variant='body' color='green' className='font-medium mb-1'>
              ✅ Encryption Key Generated Successfully
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              Your encryption key has been created and is now protecting your system data.
            </TypographyUI>
          </div>

          <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <TypographyUI variant='body' color='blue-gray' className='font-medium mb-2'>
              🔑 Recovery Code
            </TypographyUI>
            <div className='flex items-center gap-2'>
              <div className='flex-1 p-2 bg-white border border-blue-300 rounded font-mono text-sm break-all'>
                {recoveryCode}
              </div>
              <Button
                size='sm'
                variant='flat'
                color='primary'
                className='min-w-0 px-2'
                onPress={async () => {
                  try {
                    await navigator.clipboard.writeText(recoveryCode);
                    toast('Recovery code copied to clipboard', { type: 'success' });
                  } catch (error) {
                    toast('Failed to copy recovery code', { type: 'error' });
                  }
                }}
              >
                <ClipboardDocumentIcon className='h-4 w-4' />
              </Button>
            </div>
            <TypographyUI variant='body' color='gray' className='text-xs mt-2'>
              Save this recovery code securely. You&apos;ll need it if you ever need to recover your encryption key.
            </TypographyUI>
          </div>

          <div className='p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
            <TypographyUI variant='body' color='orange' className='font-medium mb-1'>
              ⚠️ Security Notice
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-xs'>
              • Store recovery code securely (password manager, safe deposit box)
              <br />
              • Store master password securely - you&apos;ll need it for recovery
              <br />
              • Never share recovery code or master password with anyone
              <br />• Recovery code cannot be regenerated - keep it safe!
            </TypographyUI>
          </div>
        </div>
      ) : (
        <div className='space-y-3'>
          <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
            <TypographyUI variant='body' color='blue-gray' className='font-medium mb-1'>
              🔐 Master Password Required
            </TypographyUI>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              Create a master password to protect your encryption key. This password will be used to encrypt the
              recovery code.
            </TypographyUI>
          </div>

          <div className='space-y-3'>
            <Controller
              name='masterPassword'
              control={control}
              render={({ field }) => (
                <PasswordInput
                  label='Master Password'
                  variant='bordered'
                  value={field.value}
                  onValueChange={field.onChange}
                  isInvalid={!!errors.masterPassword}
                  errorMessage={errors.masterPassword?.message}
                  placeholder='Enter your master password'
                />
              )}
            />

            <Controller
              name='confirmPassword'
              control={control}
              render={({ field }) => (
                <PasswordInput
                  label='Confirm Master Password'
                  variant='bordered'
                  value={field.value}
                  onValueChange={field.onChange}
                  isInvalid={!!errors.confirmPassword}
                  errorMessage={errors.confirmPassword?.message}
                  placeholder='Confirm your master password'
                />
              )}
            />
          </div>

          <div className='p-3 bg-gray-50 border border-gray-200 rounded-lg'>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              <strong>Note:</strong> The master password is used to encrypt the recovery code and is not stored on the
              system. You must remember both the master password AND recovery code to recover your encryption key. Make
              sure to store both securely.
            </TypographyUI>
          </div>
        </div>
      )}
    </ModernModal>
  );
};

/**
 * Legacy export for backward compatibility
 */
export const KeyGeneration = ({ onOpen }: { onOpen: () => void }) => <KeyGenerationButton onOpen={onOpen} />;
